import { useMemo, useState, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  BackgroundVariant,
  MarkerType,
  applyNodeChanges,
  type NodeTypes,
  type EdgeTypes,
  type NodeChange,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Card, CardHeader, CardBody, Text, Flex } from '@backstage/ui';
import { RiNodeTree } from '@remixicon/react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import { useEntityGraph, type EntityNodeData } from '../../hooks/entity-graph.hook';
import { EntityNode } from './entity-node.component';
import { RelationEdge } from './relation-edge.component';
import styles from './entity-graph.module.css';

const nodeTypes: NodeTypes = { entityNode: EntityNode };
const edgeTypes: EdgeTypes = { relationEdge: RelationEdge };
const defaultExclude = ['ownedBy'];

/** Pick the best handle side based on relative node positions. */
function getBestHandles(
  srcPos: { x: number; y: number },
  tgtPos: { x: number; y: number },
): { sourceHandle: string; targetHandle: string } {
  const dx = tgtPos.x - srcPos.x;
  const dy = tgtPos.y - srcPos.y;

  if (Math.abs(dx) > Math.abs(dy)) {
    return dx > 0
      ? { sourceHandle: 'right-src', targetHandle: 'left-tgt' }
      : { sourceHandle: 'left-src', targetHandle: 'right-tgt' };
  }
  return dy > 0
    ? { sourceHandle: 'bottom-src', targetHandle: 'top-tgt' }
    : { sourceHandle: 'top-src', targetHandle: 'bottom-tgt' };
}

/** Compute edges with dynamic handles and arrow markers from current node positions. */
function computeEdges(
  rawEdges: Edge[],
  currentNodes: Node<EntityNodeData>[],
  markerColor: string,
): Edge[] {
  const posMap = new Map(currentNodes.map(n => [n.id, n.position]));
  const marker = {
    type: MarkerType.ArrowClosed,
    width: 16,
    height: 16,
    color: markerColor,
  };

  return rawEdges.map(e => {
    const category = (e.data as Record<string, unknown> | undefined)
      ?.category as string | undefined;

    // Parent/child edges: fixed vertical handles (bottom → top)
    // Peer edges: dynamic handles based on relative position
    let handles: { sourceHandle?: string; targetHandle?: string } = {};
    if (category === 'parent' || category === 'child') {
      handles = { sourceHandle: 'bottom-src', targetHandle: 'top-tgt' };
    } else {
      const srcPos = posMap.get(e.source);
      const tgtPos = posMap.get(e.target);
      if (srcPos && tgtPos) handles = getBestHandles(srcPos, tgtPos);
    }

    return { ...e, ...handles, markerEnd: marker };
  });
}

interface EntityGraphCardProps {
  height?: number;
  direction?: 'TB' | 'LR';
  title?: string;
  showControls?: boolean;
  showMinimap?: boolean;
  excludeRelations?: string[];
}

export const EntityGraphCard = ({
  height = 300,
  direction = 'TB',
  title,
  showControls = true,
  showMinimap = false,
  excludeRelations = defaultExclude,
}: EntityGraphCardProps) => {
  const { t } = useTranslationRef(appTranslationRef);

  const { nodes: layoutNodes, edges: rawEdges } = useEntityGraph({
    direction,
    excludeRelations,
  });

  // Stable key: reset controlled state when graph structure changes
  const structureKey = useMemo(
    () => layoutNodes.map(n => n.id).sort().join(','),
    [layoutNodes],
  );

  // Controlled nodes — initialized from dagre layout, updated on drag
  const [currentNodes, setCurrentNodes] = useState(layoutNodes);

  useEffect(() => {
    setCurrentNodes(layoutNodes);
  }, [structureKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setCurrentNodes(nds => applyNodeChanges(changes, nds) as Node<EntityNodeData>[]);
  }, []);

  // Resolve CSS variable so arrow markers match edge stroke color
  const edgeColor = useMemo(
    () =>
      getComputedStyle(document.documentElement)
        .getPropertyValue('--bui-fg-secondary')
        .trim() || 'rgba(255, 255, 255, 0.7)',
    [],
  );

  // Recompute edges with handles whenever nodes move or edges change
  const edges = useMemo(
    () => computeEdges(rawEdges, currentNodes, edgeColor),
    [rawEdges, currentNodes, edgeColor],
  );

  const hasRelations = edges.length > 0;

  return (
    <Card>
      <CardHeader>
        <Flex gap="3" align="center">
          <RiNodeTree size={18} className={styles.headerIcon} />
          <Text variant="title-small" weight="bold">
            {title || t('relations.title')}
          </Text>
        </Flex>
      </CardHeader>
      <CardBody>
        {!hasRelations ? (
          <Text variant="body-medium" color="secondary">
            {t('relations.empty')}
          </Text>
        ) : (
          <div className={styles.graphContainer} style={{ height }}>
            <ReactFlow
              nodes={currentNodes}
              edges={edges}
              onNodesChange={onNodesChange}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              colorMode="dark"
              fitView
              nodesDraggable
              nodesConnectable={false}
              elementsSelectable={false}
              autoPanSpeed={4}
              fitViewOptions={{ padding: 0.3 }}
              minZoom={0.3}
              maxZoom={2}
              proOptions={{ hideAttribution: true }}
            >
              {showControls && <Controls showInteractive={false} />}
              {showMinimap && (
                <MiniMap
                  nodeColor={() => 'rgba(255, 255, 255, 0.15)'}
                  maskColor="rgba(0, 0, 0, 0.5)"
                />
              )}
              <Background
                variant={BackgroundVariant.Dots}
                gap={20}
                size={1}
                color="rgba(255, 255, 255, 0.05)"
              />
            </ReactFlow>
          </div>
        )}
      </CardBody>
    </Card>
  );
};
